import { useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, Clock3, Sparkles } from 'lucide-react';
import { addDays, Temporal, type DisplayItem } from '@autoday/core';
import { useLang } from '../i18n/i18n.ts';
import { formatTime } from '../i18n/format.ts';
import { getStore, useStore } from '../store/appStore.ts';
import { nextStep } from '../ui/nextStep.ts';
import { itemAriaLabel, timeLabel } from '../ui/format.ts';
import { navigate } from '../ui/hooks.ts';
import { useUi } from '../App.tsx';
import { Button } from '../ui/kit.tsx';
import './NextStep.css';

export function NextStep() {
  const { state } = useStore();
  const { t } = useLang();
  const ui = useUi();
  const [now, setNow] = useState(() => getStore().clock.now());
  useEffect(() => {
    const refresh = () => setNow(getStore().clock.now());
    const visible = () => { if (document.visibilityState === 'visible') refresh(); };
    const timer = setInterval(refresh, 60_000);
    document.addEventListener('visibilitychange', visible);
    window.addEventListener('pageshow', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(timer); document.removeEventListener('visibilitychange', visible);
      window.removeEventListener('pageshow', refresh); window.removeEventListener('focus', refresh);
    };
  }, []);
  const model = useMemo(() => nextStep(state, now), [state, now]);
  const tz = state.settings.timeZone;
  const duration = (minutes: number) => {
    const h = Math.floor(minutes / 60), m = minutes % 60;
    return h ? `${h} h${m ? ` ${m} min` : ''}` : `${m} min`;
  };
  const activities = (items: DisplayItem[]) => (
    <div className="autoday-next-activities">
      {items.slice(0, 2).map(item => <button type="button" key={item.uid}
        aria-label={itemAriaLabel(item, tz)} onClick={() => ui.openItem(item)}>
        <strong>{item.title}</strong><span>{timeLabel(item.timing, tz)} <ArrowUpRight size={14} aria-hidden /></span>
      </button>)}
      {items.length > 2 && <p className="text-xs text-muted">{t(`Ytterligare ${items.length - 2} samtidiga aktiviteter`, `${items.length - 2} more simultaneous activities`)}</p>}
    </div>
  );
  const gap = model.firstGap;
  const gapStart = gap ? formatTime(Temporal.Instant.fromEpochMilliseconds(gap.start), tz) : '';
  const gapEnd = gap ? formatTime(Temporal.Instant.fromEpochMilliseconds(gap.end), tz) : '';
  if (!model.current.length && !model.next.length && now.toZonedDateTimeISO(tz).hour >= 20) {
    return <section className="autoday-next-step" aria-labelledby="autoday-next-title">
      <header><h2 id="autoday-next-title">{t('Nästa steg', 'Next up')}</h2><span className="text-xs text-muted">{t('Lite lugn inför morgondagen', 'A little clarity for tomorrow')}</span></header>
      <div className="flex flex-wrap items-center justify-between gap-3 p-5"><p className="text-sm text-muted">{t('Inga fler tidsatta aktiviteter i dina visade kalendrar idag. Ta en titt på morgondagen när det passar.', 'No more timed activities in your visible calendars today. Take a look at tomorrow whenever you like.')}</p><Button onClick={() => { ui.setFocusDate(addDays(model.today, 1)); navigate('kalender'); }}>{t('Se morgondagen', 'See tomorrow')}</Button></div>
    </section>;
  }
  return (
    <section className="autoday-next-step" aria-labelledby="autoday-next-title">
      <header>
        <div className="flex items-center gap-2"><Sparkles size={18} aria-hidden className="text-accent" /><h2 id="autoday-next-title">{t('Nästa steg', 'Next up')}</h2></div>
        <span className="text-xs text-muted">{t('Din dag, här och nu', 'Your day, right now')}</span>
      </header>
      <div className="autoday-next-grid">
        <article>
          <p className="autoday-next-label"><span className="autoday-now-dot" aria-hidden />{t('Pågår nu', 'Happening now')}{model.current.length > 1 && <span className="autoday-next-count">{model.current.length} {t('samtidigt', 'at once')}</span>}</p>
          {model.current.length ? activities(model.current) : <p className="autoday-next-empty">{t('Ingen synlig tidsatt aktivitet pågår.', 'No visible timed activity is in progress.')}</p>}
        </article>
        <article>
          <p className="autoday-next-label"><Clock3 size={14} aria-hidden />{t('Härnäst', 'Coming next')}{model.minutesUntilNext !== null && <span className="autoday-next-count">{t('Om ', 'In ')}{duration(model.minutesUntilNext)}</span>}</p>
          {model.next.length ? activities(model.next) : <p className="autoday-next-empty">{t('Inga fler synliga tidsatta aktiviteter idag.', 'No more visible timed activities today.')}</p>}
        </article>
        <article className="autoday-next-free">
          <p className="autoday-next-label">{t('Lediga luckor', 'Open calendar time')}</p>
          <p className="autoday-free-total">{duration(model.freeMinutes)}</p>
          <p className="text-xs text-muted">{gap ? t(`Nästa lucka ${gapStart}–${gapEnd}`, `Next opening ${gapStart}–${gapEnd}`) : t('Ingen lucka på minst 15 minuter kvar.', 'No opening of at least 15 minutes left.')}</p>
          <Button variant="ghost" className="mt-1 px-0" icon={<ArrowUpRight size={15} aria-hidden />} onClick={() => { ui.setFocusDate(model.today); navigate('kalender'); }}>{t('Öppna kalender', 'Open calendar')}</Button>
        </article>
      </div>
      <p className="autoday-next-note">{t('Luckor på minst 15 minuter, från nu till midnatt i ', 'Openings of at least 15 minutes, from now until midnight in ')}{tz}. {t('Dolda kalendrar och bokade heldagar räknas också. Importer är ögonblicksbilder.', 'Hidden calendars and booked all-day events also count. Imports are snapshots.')}
        {model.uncertainCount > 0 && <span className="block mt-1 text-warn">{t('Vissa importerade tider är osäkra och räknas försiktigt som upptagna.', 'Some imported times are uncertain and are conservatively counted as busy.')}</span>}
      </p>
    </section>
  );
}
